import React, { FC, useEffect, useMemo } from "react";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";

import localSettings from "@app/config/LocalSettings";
import colors from "@app/styles/_colors.module.scss";
import { FEET_IN_MILE, METERS_IN_MILE } from "@app/util/consts";

import styles from "./CheckInMapDetails.module.scss";

export interface CheckInMapDetailsProps {
  checkInAccuracy: null | number;
  checkInLocation: GeoPoint;
  map?: google.maps.Map;
  recordLocation?: GeoPoint;
  verified: boolean;
}

const CheckInMapDetails: FC<CheckInMapDetailsProps> = ({
  checkInAccuracy,
  checkInLocation,
  map,
  recordLocation,
  verified,
}) => {
  const intl = useIntl();

  useEffect(() => {
    if (!map) {
      return;
    }

    const circle = new google.maps.Circle({
      center: convertGeoPointToLatLngLiteral(checkInLocation),
      fillColor: colors.slate,
      fillOpacity: 0.2,
      map,
      radius: (checkInAccuracy ?? 0) * ((checkInAccuracy ?? 0) < 1 ? 1000 : 100),
      strokeColor: colors.gray,
    });

    const bounds = circle.getBounds()!;
    if (recordLocation) {
      bounds.extend(convertGeoPointToLatLngLiteral(recordLocation));
    }
    map.fitBounds(bounds);
    return () => {
      circle.setMap(null);
    };
  }, [map, recordLocation, checkInLocation, checkInAccuracy]);

  const [distance, unit] = useMemo(() => {
    if (!checkInAccuracy) {
      return [0, ""];
    }

    const siUnits = localSettings.doesUseSiUnits();

    if (siUnits) {
      return checkInAccuracy > 1 ? [checkInAccuracy, "km"] : [checkInAccuracy * 1000, "meter"];
    }

    const miles = (checkInAccuracy * 1000) / METERS_IN_MILE;
    // if less than 1000 feet, show in feet, otherwise show in miles
    return miles <= 1000 / FEET_IN_MILE ? [miles * FEET_IN_MILE, "ft"] : [miles, "mile"];
  }, [checkInAccuracy]);

  return (
    <div className={styles.container}>
      <div className={styles.status}>
        {intl.formatMessage(
          {
            id: "dashboard.timeline.event.checkIn.verified",
            defaultMessage: "{icon} {verified, select, true {Verified} other {Unverified}}",
            description: "(Un-)Verified label in CheckIn timeline Event on Dashboard",
          },
          {
            icon: (
              <FontAwesomeIcon
                className={verified ? styles.verified : styles.unverified}
                icon={verified ? faCheckCircle : faTimesCircle}
              />
            ),
            verified,
          }
        )}
      </div>
      <div className={styles.distance}>
        {checkInAccuracy
          ? intl.formatMessage(
              {
                id: "dashboard.timeline.event.checkIn.distance",
                defaultMessage:
                  "{unit, select, km {{value, number, ::.#}km} meter {{value, number, ::.}m} mile {{value, number, ::.#} {value, plural, one {mile} other {miles}}} ft {{value, number, ::.}ft} other {{value, number, ::.}}} away",
                description: "Check-in distance in CheckIn timeline Event on Dashboard",
              },
              {
                unit,
                value: distance,
              }
            )
          : null}
      </div>
    </div>
  );
};

export default CheckInMapDetails;
